import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Timer, Download, Phone, Mail, ExternalLink, AlertTriangle } from "lucide-react";
import { T } from "@/lib/testIds";

const fmt = (amt, currency = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(amt || 0));

const BUCKET_TONE = {
  "0-30": { text: "#15803D", bg: "rgba(21,128,61,0.06)" },
  "31-60": { text: "#B45309", bg: "rgba(180,83,9,0.06)" },
  "61-90": { text: "#B91C1C", bg: "rgba(185,28,28,0.06)" },
  "90+":   { text: "#7F1D1D", bg: "rgba(127,29,29,0.08)" },
};

export default function AgeingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("0-30");
  const [dl, setDl] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/ageing");
      setData(r.data);
      const first = r.data.buckets.find((b) => b.count > 0);
      if (first) setActive(first.key);
    } catch {
      toast.error("Failed to load ageing report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    setDl(true);
    try {
      const token = localStorage.getItem("co_token");
      const imp = localStorage.getItem("co_impersonate_ws");
      const res = await fetch(`${API_BASE}/reports/ageing.csv`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(imp ? { "X-Impersonate-Workspace": imp } : {}),
        },
      });
      if (!res.ok) throw new Error("CSV download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ageing-${data?.as_of || "report"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download");
    } finally {
      setDl(false);
    }
  };

  const activeBucket = data?.buckets.find((b) => b.key === active);
  const totalCount = data?.totals?.count || 0;

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">Reports</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Ageing report</h1>
          <p className="text-xs text-[#52525B] mt-1">
            {data?.as_of && `As of ${data.as_of} · `}
            {totalCount} overdue {totalCount === 1 ? "client" : "clients"} · Chase the oldest first.
          </p>
        </div>
        <button
          data-testid={T.ageingCsvBtn}
          onClick={downloadCsv}
          disabled={dl || !totalCount}
          className="inline-flex items-center gap-2 bg-[#0A0A0B] hover:bg-[#27272A] text-white px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-50 transition-colors duration-200"
        >
          <Download className="h-4 w-4" strokeWidth={2} />
          {dl ? "Preparing…" : "Download CSV"}
        </button>
      </header>

      <div className="p-6 lg:p-10 space-y-6">
        {/* Bucket cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border co-hairline bg-white">
          {(data?.buckets || [{},{},{},{}]).map((b, i) => {
            const tone = BUCKET_TONE[b.key] || {};
            const isActive = active === b.key;
            return (
              <button
                key={b.key || i}
                type="button"
                data-testid={`${T.ageingBucket}-${b.key || i}`}
                onClick={() => b.key && setActive(b.key)}
                className={`text-left p-5 lg:p-6 border-r co-hairline last:border-r-0 transition-colors duration-200 ${
                  isActive ? "bg-[#FAFAFA]" : "bg-white hover:bg-[#FAFAFA]"
                }`}
                style={isActive ? { boxShadow: `inset 0 -3px 0 ${tone.text}` } : undefined}
              >
                <div className="flex items-center gap-2">
                  <Timer className="h-3.5 w-3.5" strokeWidth={1.75} style={{ color: tone.text }} />
                  <div className="co-label" style={{ color: tone.text }}>
                    {b.label || "—"}
                  </div>
                </div>
                <div className="mt-2 font-heading text-3xl font-bold tracking-tighter font-mono">
                  {loading ? "…" : (b.count ?? 0)}
                </div>
                <div className="text-xs text-[#52525B] mt-1">
                  {loading ? " " : fmt(b.total_amount)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected bucket table */}
        <div className="border co-hairline bg-white">
          <div className="p-5 lg:p-6 border-b co-hairline flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#B45309]" strokeWidth={1.75} />
            <div className="font-heading text-lg font-bold tracking-tight">
              {activeBucket?.label || "—"}
            </div>
            <span className="ml-auto co-label">
              {activeBucket?.count || 0} clients · {fmt(activeBucket?.total_amount || 0)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="co-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Product</th>
                  <th className="text-right">Amount</th>
                  <th>Due date</th>
                  <th className="text-right">Days overdue</th>
                  <th className="text-right">Contact</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-[#52525B]">Loading…</td></tr>
                ) : !activeBucket || activeBucket.clients.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-[#52525B] py-10">
                    Nothing overdue in this bucket. Nice work.
                  </td></tr>
                ) : activeBucket.clients.map((c) => (
                  <tr key={c.id} data-testid={T.ageingClientRow}>
                    <td>
                      <div className="font-semibold">{c.name}</div>
                      {c.segments?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.segments.slice(0, 3).map((s) => (
                            <span key={s} className="co-pill text-[10px]">{s}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-[#52525B]">{c.product_name}</td>
                    <td className="text-right font-mono font-bold">{fmt(c.amount)}</td>
                    <td className="font-mono text-[#52525B]">{c.next_due_date}</td>
                    <td className="text-right">
                      <span
                        className="font-mono font-bold"
                        style={{ color: BUCKET_TONE[activeBucket.key]?.text }}
                      >
                        {c.days_overdue}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        {c.phone && (
                          <a
                            href={`tel:${c.phone}`}
                            className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                            title={`Call ${c.phone}`}
                          >
                            <Phone className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                          </a>
                        )}
                        {c.email && (
                          <a
                            href={`mailto:${c.email}`}
                            className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                            title={`Email ${c.email}`}
                          >
                            <Mail className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                          </a>
                        )}
                        <a
                          href={`/app/clients/${c.id}`}
                          className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                          title="Open client"
                        >
                          <ExternalLink className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
